// Canvas 2D renderer. Reads a flat agent snapshot buffer (written by the sim
// worker) rather than the live World — so rendering is fully decoupled from the
// simulation thread.
//
// ---------------------------------------------------------------------------
// HOW THIS STAYS FAST WITH THOUSANDS OF ANIMALS
// ---------------------------------------------------------------------------
// Three rules, and every one of them was a measurable win:
//
// 1. ONE PASS, MANY PATHS. Agents are visited exactly once per frame and their
//    geometry is appended to one of a dozen `Path2D` buckets keyed by
//    (species × posture × condition). Each bucket is then filled once. So the
//    number of canvas draw calls is a small CONSTANT, not O(agents) — and it
//    stays constant no matter how the population grows. Never add a per-agent
//    `save()`/`restore()`, `shadowBlur`, or `fillStyle` assignment here; any one
//    of them turns the frame budget into O(agents) state changes.
//
// 2. THE TERRAIN IS AN IMAGE, NOT RECTANGLES. The grass grid is rasterised into
//    an offscreen canvas at ONE PIXEL PER CELL and blitted scaled with
//    smoothing on. That is a single `drawImage` per frame instead of ~5,000
//    `fillRect`s with a freshly built colour string each, and the bilinear
//    upscale is what turns a grid of hard squares into ground that reads as
//    terrain. The raster is rebuilt only when a new grass snapshot arrives
//    (~2.5 Hz), not every frame.
//
// 3. CULL, THEN CHOOSE A LEVEL OF DETAIL. Off-screen agents cost two compares.
//    On-screen ones get a silhouette proportional to how big they actually are
//    on screen: a speck at map scale, an oriented body when you can see it, and
//    legs/ears/tail only when zoomed in far enough that they'd be visible —
//    which is precisely when only a handful of animals are in frame.
//
// Colours come from CSS custom properties, read ONCE at construction (and on
// `refreshTheme()`), never per frame.

import { vec, type Vec2 } from "../sim/vec2";
import { SPECIES } from "../sim/species";
import {
  STRIDE,
  F_ID,
  F_X,
  F_Y,
  F_SPECIES,
  F_ENERGY,
  F_SIZE,
  F_HEADING,
  F_ACTIVITY,
  F_AGE_FRAC,
  F_SPEED,
  F_SEX,
  ACT_REST,
  ACT_TRAVEL,
} from "../sim/renderBuffer";
import { M_PER_PX } from "../sim/scale";
import { Camera } from "./camera";

const TAU = Math.PI * 2;
const tmp: Vec2 = vec();

// ---------------------------------------------------------------------------
// Palette. CSS custom property first, then these — which are the chart palette,
// so a species is the same colour everywhere in the product.
// ---------------------------------------------------------------------------
const FALLBACK_SPECIES = ["#d8ab6a", "#93a6ba"]; // deer (warm tan), wolf (cool slate)
const FALLBACK = {
  grass: "#6fbf5c",
  bg: "#0b0f0a",
  ink: "#dde5d4",
  muted: "#8a9580",
  accent: "#6fbf5c",
  warn: "#e0a458",
  danger: "#d4675a",
};

// ---------------------------------------------------------------------------
// Body plans. Top-down silhouettes: an oriented ellipse for the trunk, a circle
// for the head, a circle for the rump, and — only when zoomed right in — limbs.
// The numbers are proportions of the trunk, so everything scales with the
// animal's real mass.
// ---------------------------------------------------------------------------
interface Profile {
  lenMul: number; // trunk half-length multiplier
  aspect: number; // trunk length : width
  headFwd: number; // head centre, as a fraction of the trunk half-length
  headR: number; // head radius, as a fraction of the trunk half-width
  rumpBack: number;
  rumpR: number;
  legOut: number; // how far legs reach past the flank
  tail: number; // tail length as a fraction of half-length; 0 = none
  antler: number; // antler beam length as a fraction of head radius; 0 = none
}

const PROFILE_BY_KEY: Record<string, Profile> = {
  // A deer is deep-bodied and leggy, and carries its head high and forward.
  deer: { lenMul: 1.0, aspect: 2.85, headFwd: 1.3, headR: 0.62, rumpBack: 0.46, rumpR: 1.0, legOut: 1.9, tail: 0.22, antler: 2.1 },
  // A wolf is longer for its mass, lower, and slung between a big head and a
  // heavy brush of a tail — which is most of what tells the two apart at speed.
  wolf: { lenMul: 1.08, aspect: 3.6, headFwd: 1.28, headR: 0.8, rumpBack: 0.46, rumpR: 0.92, legOut: 1.55, tail: 0.95, antler: 0 },
};
const DEFAULT_PROFILE: Profile = {
  lenMul: 1, aspect: 3.0, headFwd: 1.05, headR: 0.7, rumpBack: 0.48, rumpR: 0.95, legOut: 1.7, tail: 0.4, antler: 0,
};

/**
 * Trunk half-length in metres for a 45 kg animal (body scale 1.0). A real deer
 * is ~1.8 m nose to tail, which at 1 px = 1 m is four pixels at full zoom-out —
 * invisible. Exaggerated ~5x so the animals are the subject of the picture; the
 * RATIOS between them stay honest.
 */
const BASE_HALF_LEN = 5.8 / M_PER_PX;

/** Below this fraction of its lifespan an animal is drawn as a juvenile. */
const JUVENILE_FRAC = 0.16;

/**
 * Reference speed in m/s for normalising F_SPEED, which the buffer carries in
 * ABSOLUTE m/s (a sprinting animal is ~18). Every visual that reacts to speed —
 * body stretch, gait rate, motion streak — wants a 0..1 fraction, and feeding it
 * raw m/s multiplied those effects by twenty: streaks became scratches across
 * the whole map and running animals stretched into slivers.
 */
const SPEED_REF = 16;

// Condition bands. Below `GAUNT` an animal is visibly failing.
const GAUNT = 0.2;
const LEAN = 0.45;

// Level-of-detail thresholds, in screen pixels of trunk half-length.
const LOD_BODY = 1.4; // below: a speck
// Tuned against the DEFAULT zoom, not against a zoomed-in view: at the opening
// camera a mid-size deer has rx ~3.8, so thresholds of 5 and 11 meant the whole
// population rendered as plain ellipses until you zoomed in. A silhouette needs
// a head to read as an animal, and it needs it at the zoom people actually use.
const LOD_DETAIL = 2.4; // above: head, rump
const LOD_LIMBS = 3.6; // above: legs, ears, tail

const SELECT_DASH = [7, 6];
const NO_DASH: number[] = [];
const SCALE_STEPS = [5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];

const FERT_BANDS = 8;

export interface GrassSnapshot {
  cols: number;
  rows: number;
  cell: number;
  max: number;
  amount: Float32Array;
  /**
   * OPTIONAL, and worth wiring: the per-cell fertility field (`Grass.fertility`).
   * Without it, thin ground and stripped-bare rich ground look identical,
   * because both simply hold little grass. With it the renderer can show
   * fullness RELATIVE TO WHAT THE CELL CAN HOLD and tint poor ground toward
   * grey — so a grazed-out meadow reads as grazed, and a barren ridge reads as
   * barren. Degrades cleanly to the old look when absent.
   */
  fertility?: Float32Array;
}

export interface RenderOptions {
  /**
   * Position in the day/night cycle as a fraction of one day, matching
   * `world.dayPhase()`: 0 = dawn, 0.25 = midday, 0.5 = dusk, 0.75 = midnight.
   * Compute as `(simTime / params.dayPeriod) % 1`.
   *
   * Omit (or pass null) and the scene renders in flat daylight.
   */
  dayPhase?: number | null;
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

export class Renderer {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  camera = new Camera();
  dpr = Math.min(2, window.devicePixelRatio || 1);
  worldW = 1600;
  worldH = 1200;

  /** See RenderOptions.dayPhase. Settable directly, or per-call via render(). */
  dayPhase: number | null = null;
  showScaleBar = true;
  showMinimap = true;

  // --- Cached theme ---------------------------------------------------------
  private nSpecies = SPECIES.length;
  private profiles: Profile[] = [];
  private speciesRgb: RGB[] = [];
  private bucketCss: string[] = []; // species × posture × condition
  private limbCss: string[] = [];
  private streakCss: string[] = [];
  private voidCss = "#0b0f0a";
  private boundsCss = "#3a4432";
  private panelCss = "rgba(12,16,11,0.82)";
  private inkCss = "#dde5d4";
  private mutedCss = "#8a9580";
  private accentRgb: RGB = { r: 111, g: 191, b: 92 };
  private shadowCss = "rgba(0,0,0,0.32)";
  private voidRgb: RGB = { r: 11, g: 15, b: 10 };
  private terrainLut = new Uint8Array(FERT_BANDS * 256 * 3);

  // --- Cached render objects (never allocated in the draw loop) -------------
  private terrain: HTMLCanvasElement | null = null;
  private terrainCtx: CanvasRenderingContext2D | null = null;
  private terrainImg: ImageData | null = null;
  private terrainSrc: Float32Array | null = null; // identity of the rasterised snapshot
  private terrainW = 0; // world extent covered by the raster
  private terrainH = 0;
  private grain: CanvasPattern | null = null;
  private grainSize = 96;
  private vignette: CanvasGradient | null = null;

  private bodyPaths: (Path2D | null)[] = [];
  private detailPaths: (Path2D | null)[] = [];
  private streakPaths: (Path2D | null)[] = [];
  private miniPaths: (Path2D | null)[] = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx = ctx;
    this.profiles = SPECIES.map((s) => PROFILE_BY_KEY[s.key] ?? DEFAULT_PROFILE);
    this.bodyPaths = new Array(this.nSpecies * 6).fill(null);
    this.detailPaths = new Array(this.nSpecies).fill(null);
    this.streakPaths = new Array(this.nSpecies).fill(null);
    this.miniPaths = new Array(this.nSpecies).fill(null);
    this.refreshTheme();
    this.grain = this.buildGrain();
  }

  /** Re-read the CSS palette (after a theme switch) and rebuild every cache. */
  refreshTheme(): void {
    let css: CSSStyleDeclaration | null = null;
    try {
      css = getComputedStyle(document.documentElement);
    } catch {
      css = null;
    }
    const v = (name: string, fb: string): string => {
      const raw = css?.getPropertyValue(name)?.trim();
      return raw ? raw : fb;
    };

    const bg = parseColor(v("--bg", FALLBACK.bg), { r: 11, g: 15, b: 10 });
    const ink = parseColor(v("--ink", FALLBACK.ink), { r: 221, g: 229, b: 212 });
    const muted = parseColor(v("--muted", FALLBACK.muted), { r: 138, g: 149, b: 128 });
    const accent = parseColor(v("--accent", FALLBACK.accent), { r: 111, g: 191, b: 92 });
    const grass = parseColor(v("--grass", FALLBACK.grass), { r: 111, g: 191, b: 92 });

    this.voidRgb = mix(bg, { r: 0, g: 0, b: 0 }, 0.35);
    this.voidCss = css3(this.voidRgb);
    this.inkCss = css3(ink);
    this.mutedCss = css3(muted);
    this.accentRgb = accent;
    this.boundsCss = rgba(mix(muted, bg, 0.55), 0.75);
    this.panelCss = rgba(mix(bg, { r: 0, g: 0, b: 0 }, 0.2), 0.86);

    // Species colours: `--deer` / `--wolf` if the stylesheet defines them, else
    // the chart palette, else whatever the datasheet says (a species added
    // later still gets a colour without touching this file).
    this.speciesRgb = SPECIES.map((s, i) =>
      parseColor(v(`--${s.key}`, FALLBACK_SPECIES[i] ?? s.color), { r: 200, g: 200, b: 200 }),
    );

    // Twelve pre-mixed body colours. Condition and posture are colour, not
    // geometry, precisely so they cost nothing per agent.
    const gaunt = mix(muted, bg, 0.25); // the colour of an animal running on empty
    this.bucketCss = [];
    this.limbCss = [];
    this.streakCss = [];
    for (let sp = 0; sp < this.nSpecies; sp++) {
      const base = this.speciesRgb[sp];
      this.limbCss[sp] = css3(mix(base, bg, 0.62));
      this.streakCss[sp] = rgba(mix(base, ink, 0.35), 0.16);
      for (let posture = 0; posture < 2; posture++) {
        // Bedded animals sit in their own shade — lying down, out of the light.
        const p = posture === 1 ? mix(base, bg, 0.34) : base;
        for (let band = 0; band < 3; band++) {
          const c = band === 2 ? p : mix(p, gaunt, band === 1 ? 0.34 : 0.66);
          this.bucketCss[sp * 6 + posture * 3 + band] = css3(c);
        }
      }
    }
    this.shadowCss = rgba(mix(bg, { r: 0, g: 0, b: 0 }, 0.6), 0.34);

    this.buildTerrainLut(bg, grass);
    this.terrainSrc = null; // force a re-raster with the new ramp
    this.buildVignette(this.viewW, this.viewH);
  }

  resize(): void {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.max(1, Math.floor(w * this.dpr));
    this.canvas.height = Math.max(1, Math.floor(h * this.dpr));
    this.buildVignette(w, h);
  }

  get viewW(): number {
    return this.canvas.clientWidth;
  }
  get viewH(): number {
    return this.canvas.clientHeight;
  }

  /** Convert a screen point to world coordinates (for click-to-pick). */
  screenToWorld(sx: number, sy: number, out: Vec2): Vec2 {
    return this.camera.screenToWorld(sx, sy, out);
  }

  // =========================================================================
  // Frame
  // =========================================================================

  render(
    data: Float32Array,
    count: number,
    grass: GrassSnapshot | null,
    selectedId: number,
    selectedSense: number,
    opts?: RenderOptions,
  ): void {
    const ctx = this.ctx;
    const vw = this.viewW;
    const vh = this.viewH;
    if (vw <= 0 || vh <= 0) return;
    const now = performance.now();

    // The camera animates itself from inside the frame, so nothing outside has
    // to drive it — `render()` is the only thing guaranteed to run every frame.
    const cam = this.camera;
    cam.minZoom = Math.min(vw / this.worldW, vh / this.worldH) * 0.3;
    cam.update(now);
    cam.clampTo(this.worldW, this.worldH, vw, vh);

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    // Opaque, not cleared: everything after this composites over a known
    // colour, which is what lets the day/night wash work as a simple overlay.
    ctx.fillStyle = this.voidCss;
    ctx.fillRect(0, 0, vw, vh);

    if (grass) this.drawTerrain(grass, vw, vh);
    this.drawBounds();
    this.drawAgents(data, count, now, vw, vh);
    if (selectedId >= 0) this.drawSelection(data, count, selectedId, selectedSense, now);

    const phase = opts?.dayPhase !== undefined ? opts.dayPhase : this.dayPhase;
    if (phase !== null && phase !== undefined && isFinite(phase)) {
      this.drawDaylight(phase, vw, vh);
    }
    if (this.vignette) {
      ctx.fillStyle = this.vignette;
      ctx.fillRect(0, 0, vw, vh);
    }

    // Overlays sit ABOVE the wash so the scale bar stays readable at midnight.
    const top = this.showScaleBar ? this.drawScaleBar(vw, vh) : 14;
    if (this.showMinimap) this.drawMinimap(data, count, vw, vh, top);
  }

  // =========================================================================
  // Terrain
  // =========================================================================

  /**
   * The grass→colour ramp, as a lookup table: [fertility band][fullness] → RGB.
   *
   * Two axes, because "how much grass is here" and "how much grass COULD be
   * here" are different facts and the picture is much better when it shows
   * both. Fullness drives the ramp from bare soil through dry stubble to a
   * dense sward; low fertility drags the whole ramp toward a grey, stony
   * olive. A stripped meadow therefore looks brown and eaten, while thin
   * ground looks thin even when it is as full as it will ever get.
   */
  private buildTerrainLut(bg: RGB, grass: RGB): void {
    const soil = mix({ r: 62, g: 48, b: 33 }, bg, 0.42); // damp bare earth
    const straw = mix({ r: 122, g: 112, b: 60 }, bg, 0.34); // sparse dry grass
    const lush = mix(grass, { r: 0, g: 0, b: 0 }, 0.18); // dense sward, a shade off neon
    const poor = mix({ r: 96, g: 98, b: 86 }, bg, 0.45); // stony, unproductive ground
    const lut = this.terrainLut;
    for (let fb = 0; fb < FERT_BANDS; fb++) {
      const fert = fb / (FERT_BANDS - 1);
      const poorMix = (1 - fert) * 0.42;
      for (let i = 0; i < 256; i++) {
        const v = i / 255;
        let c: RGB;
        if (v < 0.34) c = mix(soil, straw, smoothstep(v / 0.34));
        else c = mix(straw, lush, smoothstep((v - 0.34) / 0.66));
        c = mix(c, poor, poorMix);
        const o = (fb * 256 + i) * 3;
        lut[o] = c.r;
        lut[o + 1] = c.g;
        lut[o + 2] = c.b;
      }
    }
  }

  /** Rasterise the grass grid one pixel per cell. Only on a new snapshot. */
  private rebuildTerrain(g: GrassSnapshot): void {
    if (!this.terrain || this.terrain.width !== g.cols || this.terrain.height !== g.rows) {
      const c = document.createElement("canvas");
      c.width = Math.max(1, g.cols);
      c.height = Math.max(1, g.rows);
      const tctx = c.getContext("2d");
      if (!tctx) return;
      this.terrain = c;
      this.terrainCtx = tctx;
      this.terrainImg = tctx.createImageData(c.width, c.height);
    }
    const tctx = this.terrainCtx;
    const img = this.terrainImg;
    if (!tctx || !img) return;

    const d = img.data; // Uint8ClampedArray — clamps the mottling for free
    const lut = this.terrainLut;
    const amount = g.amount;
    const fert = g.fertility;
    const invMax = g.max > 0 ? 1 / g.max : 0;
    const n = Math.min(g.cols * g.rows, amount.length);

    for (let i = 0; i < n; i++) {
      let band = FERT_BANDS - 1;
      let f = 1;
      if (fert !== undefined && i < fert.length) {
        f = fert[i];
        if (f < 0.02) f = 0.02;
        band = (f * (FERT_BANDS - 1) + 0.5) | 0;
      }
      let v = amount[i] * invMax;
      if (fert !== undefined) v /= f; // fullness relative to what this cell holds
      if (v > 1) v = 1;
      else if (!(v > 0)) v = 0;

      const li = (band * 256 + ((v * 255) | 0)) * 3;
      // Per-cell mottling. Bilinear upscaling makes a clean field look like a
      // gradient mesh; a deterministic ±6% wobble at cell scale makes it look
      // like ground. Baked in here so it costs nothing per frame.
      const j = ((Math.imul(i + 0x9e3779b9, 0x85ebca6b) >>> 25) - 64) >> 2;
      const o = i * 4;
      d[o] = lut[li] + j;
      d[o + 1] = lut[li + 1] + j;
      d[o + 2] = lut[li + 2] + j;
      d[o + 3] = 255;
    }
    tctx.putImageData(img, 0, 0);
    this.terrainSrc = amount;
    this.terrainW = g.cols * g.cell;
    this.terrainH = g.rows * g.cell;
  }

  private drawTerrain(g: GrassSnapshot, vw: number, vh: number): void {
    if (g.amount !== this.terrainSrc) this.rebuildTerrain(g);
    const img = this.terrain;
    if (!img) return;

    const ctx = this.ctx;
    const cam = this.camera;
    const z = cam.zoom;
    cam.worldToScreen(0, 0, tmp);
    const sx = tmp.x;
    const sy = tmp.y;
    const dw = this.terrainW * z;
    const dh = this.terrainH * z;
    if (sx > vw || sy > vh || sx + dw < 0 || sy + dh < 0) return;

    // One clip for the whole terrain pass, so the raster's ceil-rounded last
    // row/column and the grain both stop at the world edge.
    ctx.save();
    ctx.beginPath();
    ctx.rect(sx, sy, this.worldW * z, this.worldH * z);
    ctx.clip();

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, sx, sy, dw, dh);

    // Fine grain, locked to the world by shifting the pattern origin, so it
    // doesn't crawl when you pan. One fill for the whole map.
    if (this.grain) {
      const t = this.grainSize;
      const ox = ((sx % t) + t) % t;
      const oy = ((sy % t) + t) % t;
      ctx.translate(ox, oy);
      ctx.fillStyle = this.grain;
      ctx.fillRect(-ox - t, -oy - t, vw + t * 2, vh + t * 2);
    }
    ctx.restore();
  }

  private drawBounds(): void {
    const ctx = this.ctx;
    const cam = this.camera;
    cam.worldToScreen(0, 0, tmp);
    ctx.strokeStyle = this.boundsCss;
    ctx.lineWidth = 1;
    ctx.strokeRect(
      Math.round(tmp.x) + 0.5,
      Math.round(tmp.y) + 0.5,
      Math.round(this.worldW * cam.zoom),
      Math.round(this.worldH * cam.zoom),
    );
  }

  // =========================================================================
  // Animals
  // =========================================================================

  private drawAgents(
    data: Float32Array,
    count: number,
    now: number,
    vw: number,
    vh: number,
  ): void {
    const ctx = this.ctx;
    const cam = this.camera;
    const z = cam.zoom;
    if (count <= 0 || z <= 0) return;

    const bodies = this.bodyPaths;
    const detail = this.detailPaths;
    const streaks = this.streakPaths;
    for (let i = 0; i < bodies.length; i++) bodies[i] = null;
    for (let i = 0; i < detail.length; i++) {
      detail[i] = null;
      streaks[i] = null;
    }
    let shadow: Path2D | null = null;

    // World-space viewport, with enough margin for the largest body.
    const invZ = 1 / z;
    const m = 24 * invZ + 24;
    const cx0 = cam.offX - m;
    const cy0 = cam.offY - m;
    const cx1 = cam.offX + vw * invZ + m;
    const cy1 = cam.offY + vh * invZ + m;

    // Gait clock. Wall-clock, not sim time: at "max" speed the sim teleports
    // and a sim-time gait would strobe.
    const gaitT = now * 0.006;
    const nSp = this.nSpecies;
    const halfBase = BASE_HALF_LEN * z;

    for (let i = 0; i < count; i++) {
      const o = i * STRIDE;
      const wx = data[o + F_X];
      const wy = data[o + F_Y];
      if (wx < cx0 || wx > cx1 || wy < cy0 || wy > cy1) continue;

      let sp = data[o + F_SPECIES] | 0;
      if (sp < 0 || sp >= nSp) sp = 0;
      const prof = this.profiles[sp];

      // --- size: mass made legible ---------------------------------------
      // sqrt(bodyScale), not bodyScale: linear in mass would make a 136 kg
      // stag seven times a 20 kg wolf and swamp the picture, while a literal
      // mass^(1/3) would make them nearly the same. The square root sits
      // between — a big animal is unmistakably big, and still an animal.
      const raw = data[o + F_SIZE];
      let scale = Math.sqrt(raw > 0 ? raw : 1);
      const age = data[o + F_AGE_FRAC];
      if (age > 0 && age < JUVENILE_FRAC) scale *= 0.5 + (0.5 * age) / JUVENILE_FRAC;

      let rx = halfBase * prof.lenMul * scale;
      if (rx < 0.75) rx = 0.75;

      const e = data[o + F_ENERGY];
      const band = e < GAUNT ? 0 : e < LEAN ? 1 : 2;
      const act = data[o + F_ACTIVITY] | 0;
      const resting = act === ACT_REST;
      const bucket = sp * 6 + (resting ? 3 : 0) + band;
      let p = bodies[bucket];
      if (p === null) p = bodies[bucket] = new Path2D();

      // --- LOD 0: a speck ---------------------------------------------------
      if (rx < LOD_BODY) {
        const s = rx * 2;
        cam.worldToScreen(wx, wy, tmp);
        p.rect(tmp.x - rx, tmp.y - rx, s, s);
        continue;
      }

      // Contact shadow, for EVERY drawn animal rather than only close-up ones.
      // At 3 px a leg is not renderable but a shadow still reads, and it is what
      // stops the herd looking like paint flecks lying on the texture.
      const hd = data[o + F_HEADING];
      const c = Math.cos(hd);
      const s = Math.sin(hd);
      // Normalised ONCE; every consumer below wants a fraction, not m/s.
      const spdAbs = data[o + F_SPEED];
      const spd = spdAbs > 0 ? Math.min(1, spdAbs / SPEED_REF) : 0;

      // Posture. A bedded animal is a compact oval — legs folded, neck down.
      // A running one stretches along its line of travel. That single
      // difference does more for realism than any amount of colour.
      let asp = prof.aspect;
      if (resting) {
        asp *= 0.6;
        rx *= 0.9;
      } else {
        asp += spd * 0.55 + (act === ACT_TRAVEL ? 0.25 : 0);
      }
      let ry = rx / asp;
      if (ry < 0.4) ry = 0.4;

      cam.worldToScreen(wx, wy, tmp);
      const px = tmp.x;
      const py = tmp.y;

      if (shadow === null) shadow = new Path2D();
      const shy = py + ry * (resting ? 0.4 : 0.7);
      shadow.moveTo(px + rx * 1.06 * c, shy + rx * 1.06 * s);
      shadow.ellipse(px, shy, rx * 1.06, ry * 1.15, hd, 0, TAU);

      // --- LOD 1: oriented trunk -------------------------------------------
      p.moveTo(px + rx * c, py + rx * s);
      p.ellipse(px, py, rx, ry, hd, 0, TAU);
      if (rx < LOD_DETAIL) continue;

      // --- LOD 2: head, rump, cast shadow ----------------------------------
      const hFwd = resting ? prof.headFwd * 0.45 : prof.headFwd;
      const hx = px + c * rx * hFwd;
      const hy = py + s * rx * hFwd;
      const hr = ry * prof.headR;
      p.moveTo(hx + hr, hy);
      p.arc(hx, hy, hr, 0, TAU);

      const bx = px - c * rx * prof.rumpBack;
      const by = py - s * rx * prof.rumpBack;
      const br = ry * prof.rumpR;
      p.moveTo(bx + br, by);
      p.arc(bx, by, br, 0, TAU);

      if (spd > 0.5 && !resting) {
        let st = streaks[sp];
        if (st === null) st = streaks[sp] = new Path2D();
        const len = rx * (0.9 + spd * 1.6);
        st.moveTo(px - c * rx * 0.5, py - s * rx * 0.5);
        st.lineTo(px - c * len, py - s * len);
      }

      if (rx < LOD_LIMBS) continue;

      // --- LOD 3: limbs, ears, tail ----------------------------------------
      let det = detail[sp];
      if (det === null) det = detail[sp] = new Path2D();
      // Perpendicular to the heading.
      const nx = -s;
      const ny = c;

      if (!resting) {
        // Diagonal trot: the leg pairs swing in opposition, which is what a
        // walking quadruped actually does and what makes the herd look alive.
        const gait = Math.sin(gaitT * (0.6 + spd * 1.8) + (i & 31) * 0.62);
        const out = ry * prof.legOut;
        const swing = rx * 0.3 * (0.25 + spd);
        for (let fb = -1; fb <= 1; fb += 2) {
          const ax = px + c * rx * 0.52 * fb;
          const ay = py + s * rx * 0.52 * fb;
          for (let side = -1; side <= 1; side += 2) {
            const g = gait * fb * side * swing;
            det.moveTo(ax, ay);
            det.lineTo(ax + nx * out * side + c * g, ay + ny * out * side + s * g);
          }
        }
      }

      // Ears: two short flicks off the head. Both sexes, both species — unlike
      // antlers, which only half the deer would honestly be entitled to.
      const eFwd = resting ? 0.1 : 0.35;
      for (let side = -1; side <= 1; side += 2) {
        det.moveTo(hx, hy);
        det.lineTo(hx + c * hr * eFwd + nx * side * hr * 1.35, hy + s * hr * eFwd + ny * side * hr * 1.35);
      }

      // Antlers: mature MALE deer only. The most recognisable thing about the
      // species, and the model already knows which animals are entitled to
      // them — drawing them on every deer would be a lie that happens to look
      // good. Two swept beams with a single fork; more tines than that is
      // invisible at any zoom this renders at.
      if (prof.antler > 0 && data[o + F_SEX] > 0.5 && age > JUVENILE_FRAC) {
        const beam = hr * prof.antler;
        for (let side = -1; side <= 1; side += 2) {
          const bxs = hx + nx * side * hr * 0.55;
          const bys = hy + ny * side * hr * 0.55;
          // Sweep back and out, the way a whitetail's main beam does.
          const tx = bxs - c * beam * 0.35 + nx * side * beam * 0.75;
          const ty = bys - s * beam * 0.35 + ny * side * beam * 0.75;
          det.moveTo(bxs, bys);
          det.lineTo(tx, ty);
          // One forward tine off the beam.
          det.moveTo(bxs + (tx - bxs) * 0.45, bys + (ty - bys) * 0.45);
          det.lineTo(
            bxs + (tx - bxs) * 0.45 + c * beam * 0.5,
            bys + (ty - bys) * 0.45 + s * beam * 0.5,
          );
        }
      }

      if (prof.tail > 0) {
        const sway = resting ? 0 : Math.sin(gaitT * 0.9 + i) * ry * 0.55;
        const tl = rx * prof.tail * (resting ? 0.5 : 1);
        det.moveTo(bx, by);
        det.lineTo(bx - c * tl + nx * sway, by - s * tl + ny * sway);
      }
    }

    // --- Six-ish draw calls for the whole population ------------------------
    if (shadow !== null) {
      ctx.fillStyle = this.shadowCss;
      ctx.fill(shadow);
    }
    const sw = Math.max(1, Math.min(12, z * 1.3));
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (let sp = 0; sp < nSp; sp++) {
      const st = streaks[sp];
      if (st === null) continue;
      ctx.strokeStyle = this.streakCss[sp];
      ctx.lineWidth = sw;
      ctx.stroke(st);
    }
    // Limbs go UNDER the trunk: only the parts that stick out should show.
    const lw = Math.max(1, Math.min(7, z * 0.5));
    for (let sp = 0; sp < nSp; sp++) {
      const det = detail[sp];
      if (det === null) continue;
      ctx.strokeStyle = this.limbCss[sp];
      ctx.lineWidth = lw;
      ctx.stroke(det);
    }
    ctx.lineCap = "butt";
    ctx.lineJoin = "miter";
    for (let b = 0; b < bodies.length; b++) {
      const p = bodies[b];
      if (p === null) continue;
      ctx.fillStyle = this.bucketCss[b];
      ctx.fill(p);
    }
  }

  // =========================================================================
  // Selection
  // =========================================================================

  private drawSelection(
    data: Float32Array,
    count: number,
    selectedId: number,
    senseRadius: number,
    now: number,
  ): void {
    for (let i = 0; i < count; i++) {
      const o = i * STRIDE;
      if (data[o + F_ID] !== selectedId) continue;

      const ctx = this.ctx;
      const cam = this.camera;
      const z = cam.zoom;
      cam.worldToScreen(data[o + F_X], data[o + F_Y], tmp);
      const px = tmp.x;
      const py = tmp.y;
      const raw = data[o + F_SIZE];
      const r = Math.max(4, BASE_HALF_LEN * z * Math.sqrt(raw > 0 ? raw : 1) * 1.25);

      // The sense radius, as a field of awareness rather than a hoop: a very
      // faint disc so you can see WHAT the animal can see, plus a slowly
      // turning dashed edge so it never reads as a static UI ring.
      const sr = senseRadius * z;
      if (senseRadius > 0 && sr > r) {
        const grad = ctx.createRadialGradient(px, py, r, px, py, sr);
        grad.addColorStop(0, rgba(this.accentRgb, 0.1));
        grad.addColorStop(1, rgba(this.accentRgb, 0.0));
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(px, py, sr, 0, TAU);
        ctx.fill();

        ctx.setLineDash(SELECT_DASH);
        ctx.lineDashOffset = -now * 0.012;
        ctx.strokeStyle = rgba(this.accentRgb, 0.4);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(px, py, sr, 0, TAU);
        ctx.stroke();
        ctx.setLineDash(NO_DASH);
        ctx.lineDashOffset = 0;
      }

      // A ring that breathes, so the selection is findable in a moving herd.
      const pulse = 3 + 1.6 * (1 + Math.sin(now * 0.005));
      ctx.strokeStyle = this.inkCss;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.arc(px, py, r + pulse, 0, TAU);
      ctx.stroke();

      let sp = data[o + F_SPECIES] | 0;
      if (sp < 0 || sp >= this.nSpecies) sp = 0;
      const label = `${SPECIES[sp].name} #${selectedId}`;
      ctx.font = "600 11px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      const tw = ctx.measureText(label).width;
      const ly = py - r - pulse - 9;
      ctx.fillStyle = this.panelCss;
      ctx.fillRect(px - tw / 2 - 5, ly - 11, tw + 10, 15);
      ctx.fillStyle = this.inkCss;
      ctx.fillText(label, px, ly);
      ctx.textAlign = "left";
      return;
    }
  }

  // =========================================================================
  // Atmosphere
  // =========================================================================

  /**
   * Wash the whole scene with the light of the hour. Sun elevation is
   * sin(2π·phase): 0 at dawn, +1 at midday, 0 at dusk, −1 at midnight — the
   * same curve the sim uses to decide when animals bed down, so the picture
   * and the behaviour agree. Kept as two flat fills; nothing per agent.
   */
  private drawDaylight(phase: number, vw: number, vh: number): void {
    const f = phase - Math.floor(phase);
    const elev = Math.sin(f * TAU);
    const ctx = this.ctx;

    const night = elev < 0 ? Math.min(1, -elev) : 0;
    if (night > 0.004) {
      ctx.fillStyle = `rgba(26,42,88,${(0.52 * Math.pow(night, 0.7)).toFixed(3)})`;
      ctx.fillRect(0, 0, vw, vh);
    }
    // Low sun: the twenty minutes either side of the horizon, when the whole
    // map goes amber. Also exactly when the deer are up and feeding.
    const golden = elev > -0.45 ? Math.max(0, 1 - Math.abs(elev) * 2.6) : 0;
    if (golden > 0.004) {
      ctx.fillStyle = `rgba(255,152,74,${(0.16 * golden).toFixed(3)})`;
      ctx.fillRect(0, 0, vw, vh);
    }
  }

  private buildVignette(w: number, h: number): void {
    if (w <= 0 || h <= 0) {
      this.vignette = null;
      return;
    }
    const inner = Math.min(w, h) * 0.44;
    const outer = Math.hypot(w, h) * 0.62;
    const g = this.ctx.createRadialGradient(w / 2, h / 2, inner, w / 2, h / 2, outer);
    g.addColorStop(0, rgba(this.voidRgb, 0));
    g.addColorStop(0.65, rgba(this.voidRgb, 0.2));
    g.addColorStop(1, rgba(this.voidRgb, 0.6));
    this.vignette = g;
  }

  /**
   * A soft mottle tile. Generated small and blown up with smoothing so the
   * grain is blotchy like ground cover rather than TV static, then repeated as
   * a pattern — one fill for the entire map, at any zoom.
   */
  private buildGrain(): CanvasPattern | null {
    const t = this.grainSize;
    const src = document.createElement("canvas");
    src.width = 24;
    src.height = 24;
    const sctx = src.getContext("2d");
    if (!sctx) return null;
    const img = sctx.createImageData(24, 24);
    const d = img.data;
    let seed = 0x9e3779b9;
    for (let i = 0; i < 24 * 24; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const n = (seed >>> 20) & 255;
      const o = i * 4;
      d[o] = n < 128 ? 0 : 255;
      d[o + 1] = d[o];
      d[o + 2] = d[o];
      d[o + 3] = 14 + (n & 15);
    }
    sctx.putImageData(img, 0, 0);

    const tile = document.createElement("canvas");
    tile.width = t;
    tile.height = t;
    const tctx = tile.getContext("2d");
    if (!tctx) return null;
    tctx.imageSmoothingEnabled = true;
    tctx.imageSmoothingQuality = "high";
    tctx.drawImage(src, 0, 0, t, t);
    return this.ctx.createPattern(tile, "repeat");
  }

  // =========================================================================
  // Overlays
  // =========================================================================

  /**
   * A real scale bar. 1 px = 1 m (see sim/scale.ts), so this is the one place
   * the viewer can find out that the map is a 4.8 km × 3.6 km stretch of country
   * and that the animals on it are the size of animals.
   *
   * Top-right, because every other corner is spoken for: Tweakpane top-left,
   * the HUD bottom-left, and the mobile panel button bottom-right.
   */
  private drawScaleBar(vw: number, vh: number): number {
    if (vw < 260 || vh < 200) return 12;
    const z = this.camera.zoom;
    let metres = SCALE_STEPS[0];
    for (let i = SCALE_STEPS.length - 1; i >= 0; i--) {
      if (SCALE_STEPS[i] * z <= 140) {
        metres = SCALE_STEPS[i];
        break;
      }
    }
    const px = metres * z;
    if (px < 24) return 12;

    const ctx = this.ctx;
    const right = vw - 14;
    const left = right - px;
    const y = 26;

    ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
    ctx.textAlign = "right";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = this.mutedCss;
    ctx.fillText(metres >= 1000 ? `${metres / 1000} km` : `${metres} m`, right, y - 5);
    ctx.textAlign = "left";

    ctx.strokeStyle = this.mutedCss;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(left + 0.5, y - 4);
    ctx.lineTo(left + 0.5, y + 0.5);
    ctx.lineTo(right + 0.5, y + 0.5);
    ctx.lineTo(right + 0.5, y - 4);
    ctx.stroke();
    return y + 12;
  }

  /**
   * A minimap, shown only once the viewport no longer holds the whole world —
   * which is the only time it tells you anything. The terrain thumbnail is the
   * grass raster we already built, so it costs one `drawImage`; the agents are
   * subsampled to a few hundred dots in two paths.
   */
  private drawMinimap(data: Float32Array, count: number, vw: number, vh: number, top: number): void {
    if (vw < 620 || vh < 380) return;
    const cam = this.camera;
    const z = cam.zoom;
    const viewW = vw / z;
    const viewH = vh / z;
    if (viewW >= this.worldW * 0.99 && viewH >= this.worldH * 0.99) return;

    const ctx = this.ctx;
    const mw = 148;
    const mh = Math.max(40, Math.round((mw * this.worldH) / this.worldW));
    const mx = vw - mw - 14;
    const my = top;

    ctx.fillStyle = this.panelCss;
    ctx.fillRect(mx - 1, my - 1, mw + 2, mh + 2);

    const kx = mw / this.worldW;
    const ky = mh / this.worldH;

    ctx.save();
    ctx.beginPath();
    ctx.rect(mx, my, mw, mh);
    ctx.clip();

    if (this.terrain && this.terrainW > 0) {
      ctx.globalAlpha = 0.85;
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(this.terrain, mx, my, this.terrainW * kx, this.terrainH * ky);
      ctx.globalAlpha = 1;
    }

    const paths = this.miniPaths;
    for (let i = 0; i < paths.length; i++) paths[i] = null;
    const step = count > 600 ? Math.ceil(count / 600) : 1;
    for (let i = 0; i < count; i += step) {
      const o = i * STRIDE;
      let sp = data[o + F_SPECIES] | 0;
      if (sp < 0 || sp >= this.nSpecies) sp = 0;
      let p = paths[sp];
      if (p === null) p = paths[sp] = new Path2D();
      p.rect(mx + data[o + F_X] * kx - 0.85, my + data[o + F_Y] * ky - 0.85, 1.7, 1.7);
    }
    for (let sp = 0; sp < paths.length; sp++) {
      const p = paths[sp];
      if (p === null) continue;
      ctx.fillStyle = css3(this.speciesRgb[sp]);
      ctx.fill(p);
    }

    // Where you are looking.
    ctx.strokeStyle = this.inkCss;
    ctx.lineWidth = 1;
    ctx.strokeRect(
      mx + cam.offX * kx + 0.5,
      my + cam.offY * ky + 0.5,
      Math.max(3, viewW * kx),
      Math.max(3, viewH * ky),
    );
    ctx.restore();

    ctx.strokeStyle = this.boundsCss;
    ctx.lineWidth = 1;
    ctx.strokeRect(mx - 1.5, my - 1.5, mw + 3, mh + 3);
  }
}

// ---------------------------------------------------------------------------
// Colour helpers. Used at construction and on theme change; never per frame.
// ---------------------------------------------------------------------------

function parseColor(input: string, fallback: RGB): RGB {
  const s = input.trim();
  if (s.startsWith("#")) {
    const hex = s.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      if (isFinite(r) && isFinite(g) && isFinite(b)) return { r, g, b };
    } else if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      if (isFinite(r) && isFinite(g) && isFinite(b)) return { r, g, b };
    }
    return fallback;
  }
  const m = s.match(/(-?[\d.]+)/g);
  if (m && m.length >= 3) {
    const r = Number(m[0]);
    const g = Number(m[1]);
    const b = Number(m[2]);
    if (isFinite(r) && isFinite(g) && isFinite(b)) return { r, g, b };
  }
  return fallback;
}

function mix(a: RGB, b: RGB, t: number): RGB {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

function css3(c: RGB): string {
  return `rgb(${clamp255(c.r)},${clamp255(c.g)},${clamp255(c.b)})`;
}

function rgba(c: RGB, a: number): string {
  return `rgba(${clamp255(c.r)},${clamp255(c.g)},${clamp255(c.b)},${a})`;
}

function clamp255(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}

function smoothstep(t: number): number {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x * x * (3 - 2 * x);
}
